import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { Dish, DishInput } from '../types';

export function useDishes() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['dishes'],
    queryFn: () => api.get<Dish[]>('/dishes'),
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['dishes'] });
    queryClient.invalidateQueries({ queryKey: ['today'] });
  }

  const createMutation = useMutation({
    mutationFn: (input: DishInput) => api.post('/dishes', input),
    onSuccess: invalidate,
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: number; input: DishInput }) => api.put(`/dishes/${id}`, input),
    onSuccess: invalidate,
  });
  const archiveMutation = useMutation({
    mutationFn: (id: number) => api.del(`/dishes/${id}`),
    onSuccess: invalidate,
  });

  return {
    dishes: data ?? [],
    loading: isLoading,
    createDish: (input: DishInput) => createMutation.mutateAsync(input),
    updateDish: (id: number, input: DishInput) => updateMutation.mutateAsync({ id, input }),
    archiveDish: (id: number) => archiveMutation.mutateAsync(id),
    reload: () => queryClient.invalidateQueries({ queryKey: ['dishes'] }),
  };
}

export function useDish(id: number | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: ['dishes', id],
    queryFn: () => api.get<Dish>(`/dishes/${id}`),
    enabled: !!id,
  });

  return { dish: data ?? null, loading: isLoading };
}
